import { useRef } from 'react'
import React = require('react')

import { CommonUtils } from '../common/common-utils'
import { Expense } from '../common/data-expenses'
import { getDataService } from '../common/data-service'
import { Enums, InvoiceExpenseCategory } from '../common/enums'
import { AugmentedCategory } from '../common/expense-category-path-builder'
import { i18n } from '../common/i18n'
import { bindInput, bindSelect } from './bind-utils'
import { Comp } from './comp'
import { getDefaultCancelButton, Modal, ModalButton } from './modal'
import { useOpenModalEvent } from './use-open-modal-event'
import { User } from './user'
import { Utils } from './utils'
import { ValidationErrors, ValidationUtils } from './validation-utils'

interface FormProps {
  expense?: Expense,
  expenseCategories?: AugmentedCategory[],
}

interface FormState {
  _id: number,
  time: string,
  description: string,
  amount: string,
  expenseCategory: InvoiceExpenseCategory,
  validationErrors: ValidationErrors,
}

export interface EditExpenseModalProps {
  modalId: string,
  expense?: Expense,
  expenseCategories: AugmentedCategory[],
  afterSave: () => void,
}

const { FormRow } = Comp
const defaultExpenseCategoryId = Enums.invoiceExpenseCategories['company vat']

class Form extends React.Component<FormProps, FormState> {
  static defaultProps = (function() {
    const now = CommonUtils.getNow()
    const date = CommonUtils.utcDateYMD(now)

    return {
      expense: {
        time: date,
        description: '',
        amount: '',
        expenseCategory: defaultExpenseCategoryId,
      },
    }
  }())

  constructor(props) {
    super(props)
    const state = CommonUtils.clone(props.expense)
    if (!this.expenseCategoryIdExists(props.expense.expenseCategory)) {
      state.expenseCategory = defaultExpenseCategoryId
    }

    this.state = state
  }

  _isMounted = false

  componentDidMount() {
    this._isMounted = true
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  save = () => {
    const expense = {
      time: this.state.time,
      description: this.state.description,
      amount: this.state.amount.toString(),
      expenseCategory: this.state.expenseCategory,
    }

    return ValidationUtils.clear(this)
    .then<unknown>(() => {
      const DataService = getDataService()

      if (this.state._id) {
        return DataService.Expenses.update(this.state._id, expense)
      } else {
        return DataService.Expenses.create(expense)
      }
    })
    .catch((error) => ValidationUtils.check(this, error))
  }

  expenseCategoryIdExists = (id) => {
    const category = CommonUtils.findById(this.props.expenseCategories, id)
    return category ? true : false
  }

  renderTimeRow = () => {
    return (
      <FormRow>
        {i18n.t('common.date')}
        {bindInput(this, 'time')}
        {ValidationUtils.render(this.state.validationErrors, 'time')}
      </FormRow>
    )
  }

  renderDescriptionRow = () => {
    return (
      <FormRow>
        {i18n.t('common.description')}
        {bindInput(this, 'description', { className: 'input-wide' })}
        {ValidationUtils.render(this.state.validationErrors, 'description')}
      </FormRow>
    )
  }

  renderAmountRow = () => {
    return (
      <FormRow>
        {i18n.t('common.amount')}
        {bindInput(this, 'amount', { className: 'input-narrow', validator: Utils.nonNegativeDecimalValidator })}
        {ValidationUtils.render(this.state.validationErrors, 'amount')}
      </FormRow>
    )
  }

  renderExpenseCategoryDropdown = () => {
    if (this.props.expenseCategories) {
      const lang = User.getLanguage()
      const opts = this.props.expenseCategories.map(function(expenseCategory) {
        return (
          <option key={expenseCategory._id} value={expenseCategory._id}>
            {expenseCategory.paths[lang]}
          </option>
        )
      })
      return bindSelect(this, 'expenseCategory', { className: 'input-wide' }, opts)
    } else {
      return <img src="img/loading.gif" />
    }
  }

  renderExpenseCategoryRow = () => {
    return (
      <FormRow>
        {i18n.t('common.category')}
        {this.renderExpenseCategoryDropdown()}
        {ValidationUtils.render(this.state.validationErrors, 'expenseCategory')}
      </FormRow>
    )
  }

  render() {
    return (
      <div id="form-exp">
        {this.renderTimeRow()}
        {this.renderDescriptionRow()}
        {this.renderAmountRow()}
        {this.renderExpenseCategoryRow()}
      </div>
    )
  }
}

export const EditExpenseModal = (props: EditExpenseModalProps) => {
  const [isVisible, close] = useOpenModalEvent(props.modalId)
  const formRef = useRef<Form>(null)

  if (!isVisible) {
    return <div />
  }

  const buttons: ModalButton[] = [
    {
      id: 'btn-save',
      title: i18n.t('action.save'),
      onClick: async () => {
        await formRef.current.save()
        await props.afterSave()
        close()
      },
    },
    getDefaultCancelButton(),
  ]

  return (
    <div>
      <Modal
        title={i18n.t('accounting.expenses.expense')}
        dialogClassName="narrow"
        closeModal={close}
        buttons={buttons}
      >
        <Form
          ref={formRef}
          expense={props.expense}
          expenseCategories={props.expenseCategories}
        />
      </Modal>
    </div>
  )
}
